import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import AuthContext from './authContext';
import {
  getUserPermissions,
  hasRouterAccess,
  hasActionAccess,
  isAdmin,
  UserPermission,
  ROUTER_KEYS,
  ACTION_KEYS,
} from '../api/modules/permission';

// ============ Context 类型定义 ============

export interface IPermissionContextProps {
  /** 用户权限数据 */
  permissions: UserPermission | null;
  /** 权限加载状态 */
  loading: boolean;
  /** 权限加载错误 */
  error: string | null;
  /** 刷新权限 */
  refreshPermissions: () => Promise<void>;
  /** 检查是否有路由权限 */
  canAccessRouter: (routerKey: string) => boolean;
  /** 检查是否有操作权限 */
  canDoAction: (actionKey: string) => boolean;
  /** 是否是管理员 */
  isUserAdmin: boolean;
}

// ============ Context 创建 ============

const PermissionContext = createContext<IPermissionContextProps>({
  permissions: null,
  loading: true,
  error: null,
  refreshPermissions: async () => {},
  canAccessRouter: () => false,
  canDoAction: () => false,
  isUserAdmin: false,
});

// ============ Provider 组件 ============

interface IPermissionContextProviderProps {
  children: ReactNode;
}

export const PermissionContextProvider: FC<IPermissionContextProviderProps> = ({ children }) => {
  const { isAuthenticated, authLoading } = useContext(AuthContext);
  const [permissions, setPermissions] = useState<UserPermission | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 获取用户权限
  // 注意：不再需要 userEmail，后端会从 JWT token 自动解析
  const fetchPermissions = useCallback(async () => {
    // 等待认证加载完成
    if (authLoading) {
      console.log('[PermissionContext] 等待认证加载...');
      return;
    }

    // 调试日志
    console.log('[PermissionContext] 认证状态:', { isAuthenticated });

    if (!isAuthenticated) {
      console.log('[PermissionContext] 跳过权限获取: 用户未登录');
      setPermissions(null);
      setLoading(false);
      return;
    }

    console.log('[PermissionContext] 开始获取权限...');
    setLoading(true);
    setError(null);

    try {
      const userPermissions = await getUserPermissions();
      console.log('[PermissionContext] 权限获取成功:', userPermissions);
      setPermissions(userPermissions);
    } catch (err) {
      console.error('[PermissionContext] 权限获取失败:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  // 认证状态变化时获取权限
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // 检查路由权限
  const canAccessRouter = useCallback(
    (routerKey: string): boolean => {
      return hasRouterAccess(permissions, routerKey);
    },
    [permissions]
  );

  // 检查操作权限
  const canDoAction = useCallback(
    (actionKey: string): boolean => {
      return hasActionAccess(permissions, actionKey);
    },
    [permissions]
  );

  // 是否是管理员
  const isUserAdmin = useMemo(() => isAdmin(permissions), [permissions]);

  const value = useMemo(
    () => ({
      permissions,
      loading,
      error,
      refreshPermissions: fetchPermissions,
      canAccessRouter,
      canDoAction,
      isUserAdmin,
    }),
    [permissions, loading, error, fetchPermissions, canAccessRouter, canDoAction, isUserAdmin]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

// ============ Hook ============

/**
 * 使用权限 Context 的 Hook
 */
export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionContextProvider');
  }
  return context;
};

// ============ 导出常量 ============

export { ROUTER_KEYS, ACTION_KEYS };

export default PermissionContext;
